import { PROGRESS_BAR_COLORS } from '@/constants/customerLoyaltyProgram';
import { URLS } from '@/constants/urls';
import { dateToStringFormat } from '@/utils/dateUtils';

export const getContent = (
	sectionData,
	loggedUser,
	civility,
	civilityOther,
	lastName,
	levelAmountProgress,
	lastOrderDate,
	currentLevel,
	nextLevel,
	t,
	locale,
	currencyISO,
	dateFormat,
) => {
	let titleData = null;
	let descriptionData = null;
	let btnLabelData = null;
	let btnUrlData = null;

	const {
		titleNoUser,
		descriptionNoUser,
		btnLabelNoUser,
		btnUrlNoUser,
		titleUserAllLevel,
		descriptionUserAllLevel,
		btnLabelUserAllLevel,
		btnUrlUserAllLevel,
		titleUserLevelProgress,
		descriptionUserLevelProgress,
		btnLabelUserLevelProgress,
		btnUrlUserLevelProgress,
		titleUserNoLevel,
		descriptionUserNoLevel,
		btnLabelUserNoLevel,
		btnUrlUserNoLevel,
		titleUserNoOrder,
		descriptionUserNoOrder,
		btnLabelUserNoOrder,
		btnUrlUserNoOrder,
	} = sectionData;

	if (!loggedUser) {
		titleData = titleNoUser;
		descriptionData = descriptionNoUser;
		btnLabelData = btnLabelNoUser;
		btnUrlData = btnUrlNoUser;
	} else if (!nextLevel) {
		titleData = titleUserAllLevel;
		descriptionData = descriptionUserAllLevel;
		btnLabelData = btnLabelUserAllLevel;
		btnUrlData = btnUrlUserAllLevel;
	} else if (currentLevel && nextLevel) {
		titleData = titleUserLevelProgress;
		descriptionData = descriptionUserLevelProgress;
		btnLabelData = btnLabelUserLevelProgress;
		btnUrlData = btnUrlUserLevelProgress;
	} else if (!currentLevel && levelAmountProgress > 0) {
		titleData = titleUserNoLevel;
		descriptionData = descriptionUserNoLevel;
		btnLabelData = btnLabelUserNoLevel;
		btnUrlData = btnUrlUserNoLevel;
	} else {
		titleData = titleUserNoOrder;
		descriptionData = descriptionUserNoOrder;
		btnLabelData = btnLabelUserNoOrder;
		btnUrlData = btnUrlUserNoOrder;
	}

	return {
		title: replaceVariableInContent(
			titleData,
			loggedUser,
			civility,
			civilityOther,
			lastName,
			levelAmountProgress,
			lastOrderDate,
			currentLevel,
			nextLevel,
			t,
			locale,
			currencyISO,
			dateFormat,
		),
		description: replaceVariableInContent(
			descriptionData,
			loggedUser,
			civility,
			civilityOther,
			lastName,
			levelAmountProgress,
			lastOrderDate,
			currentLevel,
			nextLevel,
			t,
			locale,
			currencyISO,
			dateFormat,
		),
		btnLabel:
			btnLabelData ??
			replaceVariableInContent(
				btnLabelData,
				loggedUser,
				civility,
				civilityOther,
				lastName,
				levelAmountProgress,
				lastOrderDate,
				currentLevel,
				nextLevel,
				t,
				locale,
				currencyISO,
				dateFormat,
			),
		btnUrl:
			btnUrlData ??
			replaceVariableInContent(
				btnUrlData,
				loggedUser,
				civility,
				civilityOther,
				lastName,
				levelAmountProgress,
				lastOrderDate,
				currentLevel,
				nextLevel,
				t,
				locale,
				currencyISO,
				dateFormat,
			),
	};
};

export const replaceVariableInContent = (
	content,
	loggedUser,
	civility,
	civilityOther,
	lastName,
	levelAmountProgress,
	lastOrderDate,
	currentLevel,
	nextLevel,
	t,
	locale,
	currencyISO,
	dateFormat,
) => {
	let contentFormat = content;

	contentFormat = contentFormat?.replace(
		'{user}',
		`${
			civility === 'other' && civilityOther
				? civilityOther
				: civility
					? t(`forms.civility.${civility}`) !== `forms.civility.${civility}`
						? t(`forms.civility.${civility}`)
						: civility
					: t('forms.civility.mrs') + '/' + t('forms.civility.mr')
		} ${lastName}`.toUpperCase(),
	);

	contentFormat = contentFormat?.replace('{current_level_title}', currentLevel?.title);
	contentFormat = contentFormat?.replace('{current_level_desc}', currentLevel?.description.toLowerCase());

	contentFormat = contentFormat?.replace('{next_level_title}', nextLevel?.title);
	contentFormat = contentFormat?.replace('{next_level_desc}', nextLevel?.description.toLowerCase());

	contentFormat = contentFormat?.replace(
		'{amount_to_spend}',
		displayPrice(getAmountLeftToSpend(levelAmountProgress, currentLevel?.amount, nextLevel?.amount), locale, currencyISO),
	);

	contentFormat = contentFormat?.replace('{last_order_date}', lastOrderDate ? dateToStringFormat(lastOrderDate, dateFormat) : '');

	return contentFormat;
};

export const displayPrice = (price, locale, currencyISO) => {
	return new Intl.NumberFormat(locale, { style: 'currency', currency: currencyISO, minimumFractionDigits: 0 }).format(price);
};

export const getAmountLeftToSpend = (levelAmountProgress, currentLevelAmount, nextLevelAmount) => {
	return nextLevelAmount - (currentLevelAmount || 0) - levelAmountProgress;
};

export const getProgressBarProps = (sectionData, loggedUser, levelAmountProgress, currentLevel, nextLevel, locale, currencyISO) => {
	let leadingContent = {
		iconBackgroundColor: PROGRESS_BAR_COLORS.UNCOMPLETED,
	};
	let trailingContent = {
		iconBackgroundColor: PROGRESS_BAR_COLORS.UNCOMPLETED,
		icon: URLS.LOCK_PREMIUM_DISABLE,
	};
	let centeredDescription = false;

	if (!loggedUser) {
		leadingContent = {
			...leadingContent,
			icon: URLS.ICON_USER_DISABLE,
			label: sectionData.labelLeadingContentNoUser,
			paragraph: sectionData.paragraphLeadingContentNoUser,
		};
		centeredDescription = true;
	} else if (!nextLevel) {
		leadingContent = {
			...leadingContent,
			icon: URLS.LOCK_PREMIUM_OPEN_WHITE,
			iconBackgroundColor: PROGRESS_BAR_COLORS.COMPLETED,
			label: sectionData.labelLeadingContentUserAllLevel,
		};
		trailingContent = {
			...trailingContent,
			icon: URLS.ICON_USER_WHITE,
			iconBackgroundColor: PROGRESS_BAR_COLORS.COMPLETED,
		};
		centeredDescription = true;
	} else if (currentLevel && nextLevel) {
		leadingContent = {
			...leadingContent,
			icon: URLS.LOCK_PREMIUM_OPEN_WHITE,
			iconBackgroundColor: PROGRESS_BAR_COLORS.COMPLETED,
			label: currentLevel.description,
			paragraph: displayPrice(currentLevel.amount, locale, currencyISO),
			badge: currentLevel.color,
		};
		trailingContent = {
			...trailingContent,
			label: nextLevel.description,
			paragraph: displayPrice(nextLevel.amount, locale, currencyISO),
			badge: nextLevel.color,
		};
	} else if (!currentLevel && levelAmountProgress > 0) {
		leadingContent = {
			...leadingContent,
			icon: URLS.ICON_USER_WHITE,
			iconBackgroundColor: PROGRESS_BAR_COLORS.COMPLETED,
			label: sectionData.labelLeadingContentUserNoLevel,
			paragraph: sectionData.paragraphLeadingContentUserNoLevel,
		};
		trailingContent = {
			...trailingContent,
			label: nextLevel.description,
			paragraph: displayPrice(nextLevel.amount, locale, currencyISO),
			badge: nextLevel.color,
		};
	} else {
		leadingContent = {
			...leadingContent,
			icon: URLS.ICON_USER_DISABLE,
			label: sectionData.labelLeadingContentNoUser,
			paragraph: sectionData.paragraphLeadingContentNoUser,
		};
		centeredDescription = true;
	}

	return {
		leadingContent,
		trailingContent,
		centeredDescription,
		cursorValue: nextLevel ? getCursorValue(levelAmountProgress || 0, locale, currencyISO, currentLevel, nextLevel) : null,
	};
};

export const getPercentProgress = (loggedUser, levelAmountProgress, currentLevel, nextLevel) => {
	if (!loggedUser || (levelAmountProgress === 0 && nextLevel)) {
		return 0;
	} else if (currentLevel && nextLevel) {
		return (levelAmountProgress / (nextLevel?.amount - currentLevel?.amount)) * 100;
	} else if (!currentLevel) {
		return (levelAmountProgress / nextLevel?.amount) * 100;
	} else if (!nextLevel) {
		return 100;
	}
};

export const getCursorValue = (levelAmountProgress, locale, currencyISO, currentLevel, nextLevel) => {
	if (currentLevel && currentLevel?.amount > 0 && nextLevel) {
		levelAmountProgress += currentLevel.amount;
	}
	return displayPrice(levelAmountProgress, locale, currencyISO);
};
